import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import KNDialog from 'components/KNDialog/KNDialog'
import KNDialogFormErrors from 'components/KNDialog/KNDialogFormErrors'
import KNFormCheckbox from 'components/KNForm/KNFormCheckbox'
import { analyticsEvent } from 'global/helpers/analytics'
import { handleFormSubmission, processDefaultValues, processServerErrorMessages } from 'global/helpers/form'
import { type ReactElement, useEffect } from 'react'
import { type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { booleanFormValuesToEnum, enumToBooleanFormValues } from './CarrierProfile.helpers'
import { CARRIER_PROFILE_QUERY_KEY, editCarrierProfile } from './CarrierProfile.service'
import { type CarrierProfile, Qualification } from './CarrierProfile.types'

interface SelectQualificationsDialogPayload {
  readonly carrierProfile: CarrierProfile,
}

export interface SelectQualificationsDialogProps {
  readonly payload: SelectQualificationsDialogPayload
  readonly open: boolean
  readonly onAction: () => void
  readonly onClose: () => void
}

type QualificationsFormValues = {
  [K in Qualification]: boolean
}

const SelectQualificationsDialog = ({
  payload,
  open,
  onAction,
  onClose,
}: SelectQualificationsDialogProps): ReactElement => {
  const { t } = useTranslation()
  const { handleSubmit, reset, control, formState, setError } = useForm<QualificationsFormValues>()
  const allQualifications = Object.values(Qualification)
  const queryClient = useQueryClient()
  const updateCarrierProfileMutation = useMutation({
    mutationFn: async (data: QualificationsFormValues) => {
      await editCarrierProfile({
        ...payload.carrierProfile,
        qualifications: booleanFormValuesToEnum<QualificationsFormValues, Qualification>(data),
      })
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [CARRIER_PROFILE_QUERY_KEY] }),
  })

  useEffect(() => {
    if (open) {
      reset(
        processDefaultValues(
          enumToBooleanFormValues<Qualification, QualificationsFormValues>(allQualifications, payload.carrierProfile.qualifications),
        ),
      )
    }
  }, [open])

  const onSubmit: SubmitHandler<QualificationsFormValues> = async (data: QualificationsFormValues): Promise<void> => {
    await handleFormSubmission(() => updateCarrierProfileMutation.mutateAsync(data), {
      onError: (error) => setError('root', processServerErrorMessages(error)),
      onSuccess: () => {
        onAction()
        analyticsEvent('polestar_carrier_profile_qualifications_updated')
      },
    })
  }

  return (
    <KNDialog
      open={open}
      onClose={onClose}
      closeLabel={t('general.cancel')}
      preventClosing={formState.isSubmitting}
      title={t('screens.cs.carrier_profile.company.select_qualifications')}
      actions={
        <Button
          type="submit"
          color="secondary"
          variant="contained"
          loading={formState.isSubmitting}
        >
          {t('general.save')}
        </Button>
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <KNDialogFormErrors errors={formState.errors?.root} />
      <Stack direction="column" spacing={1}>
        {allQualifications.map((qualification) => (
          <KNFormCheckbox
            key={qualification}
            name={qualification}
            control={control}
            label={t(`screens.cs.carrier_profile.qualifications.${qualification}`)}
          />
        ))}
      </Stack>
    </KNDialog>
  )
}

export default SelectQualificationsDialog
